// @ts-nocheck
import {
    ActionReasonDto,
    EmployeeDto,
    InventoryDto, StocktakeDto,
    StocktakeItemDto
} from "@/PageComponents/Stock Take/StockTake.model";
import StocktakeForm from "@/PageComponents/Stock Take/StockTakeForm";
import {Box, Card, Flex} from "@mantine/core";
import React, {useContext, useEffect, useMemo, useState} from "react";
import SCMessageBarContext from "@/utils/contexts/sc-message-bar-context";
import constants from "@/utils/constants";
import Breadcrumbs from "@/components/breadcrumbs";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {notifications} from "@mantine/notifications";
import StockService from "@/services/stock/stock-service";
import {useRouter} from "next/router";
import Fetch from "@/utils/Fetch";
import * as Enums from "@/utils/enums";
import Storage from "@/utils/storage";
import Helper from "@/utils/helper";
import PermissionService from "@/services/permission/permission-service";
import featureService from "@/services/feature/feature-service";
import { createFileRoute } from '@tanstack/react-router'

interface StocktakeItemFormProps {
    onSubmit: (values: Partial<StocktakeItemDto>) => void;
    onCancel: () => void;
    stocktakeId: string;
    inventoryItems: InventoryDto[];
    actionReasons: ActionReasonDto[];
    employees: EmployeeDto[];
    initialValues?: Partial<StocktakeItemDto>;
}

function EditStockPage() {

    const router = useRouter();

    // Fetch the feature flag to check if stock take is enabled.
    // Redirect to home page if the feature is not available.
    const hasStockTakeQuery = useQuery(['hasStockTake'], () => featureService.getFeature(constants.features.STOCK_TAKE))

    // Redirect if stock take feature is not available
    useEffect(() => {
        if (hasStockTakeQuery.data === null) {
            router.replace('/');
        }
    }, [hasStockTakeQuery.data])

    const { draftData } = router.query; // TODO(tanstack-router): verify router.query → useParams/useSearch

    const queryClient = useQueryClient();

    const stockTakeCopyMutation = useMutation({
        mutationFn: StockService.copyStockTake,
        onSuccess: (data: any) => {
            // Invalidate and refetch relevant queries
            queryClient.invalidateQueries({ queryKey: ['stocktake'] });

            notifications.show({
                title: "Success",
                message: "Stock take successfully copied",
                color: "scBlue"
            });

            router.replace(`/stock-take/${data.ID}`);
        },
        onError: (error: Error) => {
            notifications.show({
                title: "Error",
                message: error.message || "Failed to copy stock take",
                color: "yellow"
            });
        }
    });

    const { data: stocktake, isLoading } = useQuery<StocktakeDto>({
        queryKey: ['stockTakeItems', router.query.id], // TODO(tanstack-router): verify router.query → useParams/useSearch
        queryFn: async () => {
            // Don't make the request if there's no ID
            if (!router.query.id) return null; // TODO(tanstack-router): verify router.query → useParams/useSearch

            // Make the API call to fetch StockTakeItems for the given stockTake ID
            const response = await Fetch.get({
                url: `/Stocktake/` + router.query.id, // TODO(tanstack-router): verify router.query → useParams/useSearch
            });

            if (!response.ID) {
                Error(response.serverMessage || response.message || 'Failed to fetch stock take items');
            }

            return response;
        },
        // Only run the query if we have a router.query.id // TODO(tanstack-router): verify router.query → useParams/useSearch
        enabled: !!router.query.id, // TODO(tanstack-router): verify router.query → useParams/useSearch
        onSuccess: (data: any) => {console.log(data)}
    });

    const messageBarContext = useContext(SCMessageBarContext);

    const onSubmit = (values: any) => {
        stockTakeCopyMutation.mutate(values);
    }

    const [accessStatus, setAccessStatus] = useState(Enums.AccessStatus.None);

    const getAccessStatus = () => {
        let subscriptionInfo = Storage.getCookie(Enums.Cookie.subscriptionInfo);
        if (subscriptionInfo) {
            setAccessStatus(subscriptionInfo.AccessStatus);
        }
    };

    const [stockAdmin] = useState(PermissionService.hasPermission(Enums.PermissionName.StockTakeManager));

    useEffect(() => {
        getAccessStatus();
    }, []);

    useEffect(() => {
        if (accessStatus === Enums.AccessStatus.LockedWithOutAccess || !stockAdmin) {
            Helper.nextRouter(router.replace, "/");
        }

    }, [accessStatus, stockAdmin]);

    const onCancel = () => {
        router.replace(`/stock-take/${router.query.id}`); // TODO(tanstack-router): verify router.query → useParams/useSearch
    }

    return (
        <Box>
            <Flex h={28} align={'center'} px={'lg'}>
                {
                    stocktake &&
                    <Breadcrumbs currPage={{ text: 'Copy ' + stocktake?.Name, link: `/stock-take/${router.query.id}/copy`, type: 'edit' }} /> // TODO(tanstack-router): verify router.query → useParams/useSearch
                }
            </Flex>
            <Box
                mih={`calc(100vh - 40px - 30px${((messageBarContext as any)?.isActive ? ` - ${constants.messageBarMargin}px` : "")})`}
                bg={'gray.1'}
                py={{base: 0, lg: 'md', xl: 'lg'}}
            >
                <Card mx={'auto'} bg={'white'} p={'md'} mt={0} shadow={'xs'} radius={5} maw={1100}>
                    {
                        (stocktake) &&
                        <StocktakeForm
                            customTitle={'Data will be copied from ' + stocktake.Name}
                            isCopyMode={true}
                            submitting={stockTakeCopyMutation.isLoading}
                            initialValues={{...stocktake, Name: undefined, ScheduledDate: new Date()}}
                            onSubmit={onSubmit}
                            onCancel={onCancel}
                        />
                    }
                </Card>
            </Box>
        </Box>
    );
}

export default EditStockPage;

export const Route = createFileRoute('/_authenticated/stock-take/$id/copy')({ component: EditStockPage })
